import { getAssociatedTokenAddressSync } from '@solana/spl-token';
import { PublicKey } from '@solana/web3.js';
import type BN from 'bn.js';
import {
  deriveBackerShareMintPda,
  deriveMarketPda,
  derivePlatformFeeVaultPda,
} from '../pda/derivations.js';

interface MarketCreationAddresses {
  market: PublicKey;
  outcomeMetadata: PublicKey;
  marketQuoteVault: PublicKey;
  backerShareMint: PublicKey;
  creatorBackerShareAta: PublicKey;
  platformFeeVault: PublicKey;
  adminConfig: PublicKey;
  pdaBumps: {
    market: number;
    outcomeMintAuthority: number;
    marketQuoteVaultAuthority: number;
    marketQuoteVault: number;
    backerShareMint: number;
  };
}

/** Current creation accounts and canonical bumps; no account reads. */
export function deriveMarketCreationAddresses(
  creator: PublicKey,
  marketNonce: BN,
  marketConfig: PublicKey,
  programId: PublicKey,
): MarketCreationAddresses {
  const [market, marketBump] = deriveMarketPda(creator, marketNonce, programId);
  const [, outcomeMintAuthorityBump] = deriveCreationPda(
    'outcome_mint_authority',
    market,
    programId,
  );
  const [, marketQuoteVaultAuthorityBump] = deriveCreationPda(
    'market_quote_vault_authority',
    market,
    programId,
  );
  const [marketQuoteVault, marketQuoteVaultBump] = deriveCreationPda(
    'market_quote_vault',
    market,
    programId,
  );
  const [backerShareMint, backerShareMintBump] = deriveBackerShareMintPda(market, programId);
  const pdaBumps = {
    market: marketBump,
    outcomeMintAuthority: outcomeMintAuthorityBump,
    marketQuoteVaultAuthority: marketQuoteVaultAuthorityBump,
    marketQuoteVault: marketQuoteVaultBump,
    backerShareMint: backerShareMintBump,
  };
  if (Object.values(pdaBumps).reduce((sum, bump) => sum + 256 - bump, 0) > 40) {
    throw new Error('Market creation PDA derivation budget exceeded; choose another market nonce');
  }
  return {
    market,
    outcomeMetadata: deriveCreationPda('outcome_metadata', market, programId)[0],
    marketQuoteVault,
    backerShareMint,
    creatorBackerShareAta: getAssociatedTokenAddressSync(backerShareMint, creator),
    platformFeeVault: derivePlatformFeeVaultPda(marketConfig, programId)[0],
    adminConfig: PublicKey.findProgramAddressSync([Buffer.from('admin_config')], programId)[0],
    pdaBumps,
  };
}

export function deriveCreationPda(
  seed: string,
  market: PublicKey,
  programId: PublicKey,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync([Buffer.from(seed), market.toBuffer()], programId);
}
